/**
 * Strategy Transformation Service
 * 
 * Transforms complex frontend StrategyWizardDialog data into backend-compatible format
 * and vice versa. Handles the mapping between the sophisticated UI state and the
 * simplified backend API schema.
 */

import strategyValidationService from './strategyValidationService';
import { EXPIRY_TYPE, UNDERLYING_PCT } from '../utils/strategy/strategyConstants';
import { WEEKDAYS } from '../constants/tradingConstants';
import { parseStrikeValue, validateStrikeFormat, formatStrikeForDisplay } from '../utils/strategy/strikeValueParser';
import { getProductTypeValidationError, autoCorrectProductType } from '../utils/strategy/productTypeValidation';
import { validatePercentageOfUnderlying } from '../utils/strategy/methodValidators';
import { SelectionMethod, Leg, ExpiryType } from '../types/strategy';

// Using Leg interface from types/strategy.ts

interface FrontendStrategyConfig {
  entryTimeHour: string;
  entryTimeMinute: string;
  exitTimeHour: string;
  exitTimeMinute: string;
  rangeBreakout: boolean;
  rangeBreakoutTimeHour: string;
  rangeBreakoutTimeMinute: string;
  /** Opt-in carry-forward for ORB unfilled SL-trigger entries (NRML only). */
  orbContinueOvernight: boolean;
  moveSlToCost: boolean;
  // Product type configuration
  productType: 'MIS' | 'NRML';
  // New trading type configuration
  tradingType: 'INTRADAY' | 'POSITIONAL';
  intradayExitMode: 'SAME_DAY' | 'NEXT_DAY_BTST';
  /**
   * BTST/STBT only: when true and today is the underlying's expiry day for the
   * strategy's expiry_type, the entry is placed against the NEXT contract
   * (next weekly / next monthly) so STBT/BTST can still run. Ignored unless
   * tradingType=INTRADAY and intradayExitMode=NEXT_DAY_BTST.
   */
  expiryDayRollover: boolean;
  entryDays: string[];
  exitDays: string[];
  // INTRADAY entry selection mode (optional for back-compat). WEEKDAY defaults
  // when undefined. DTE allowed only for current-weekly expiries in v1.
  entrySelectionMode?: 'WEEKDAY' | 'DTE';
  entryDtes?: number[];
  entryTradingDaysBeforeExpiry: number;
  exitTradingDaysBeforeExpiry: number;
  // Strategy-level expiry type — one of weekly / next_weekly / monthly / next_monthly.
  expiryType: ExpiryType;
  targetProfit: {
    type: 'TOTAL_MTM' | 'COMBINED_PREMIUM_PERCENT';
    value: number;
  };
  mtmStopLoss: {
    type: 'TOTAL_MTM' | 'COMBINED_PREMIUM_PERCENT';
    value: number;
  };
}

interface FrontendStrategyData {
  strategyId?: string;  // Optional - undefined for creation, defined for editing
  basketId: string;
  strategyName: string;
  index: string;
  config: FrontendStrategyConfig;
  legs: Leg[];
}

// Backend API schema
interface BackendLeg {
  leg_id?: string;
  option_type: 'CE' | 'PE';
  action: 'BUY' | 'SELL';
  lots: number;

  // Dynamic strike selection criteria (flat structure)
  selection_method: SelectionMethod;
  selection_value?: number;        // Numeric value for all methods
  selection_operator?: 'CLOSEST' | 'GTE' | 'LTE'; // For premium methods only

  // Associate-hedge. On SAVE the frontend sends is_hedge + hedge_for_leg_index
  // (1-based position of the target main leg in the sent legs array). On LOAD the
  // backend returns is_hedge + the resolved hedge_for_leg_id (the main's leg_id);
  // hedge_for_leg_id is read-only — the transformer resolves it back to the
  // target leg's frontend id.
  is_hedge?: boolean;
  hedge_for_leg_index?: number;
  hedge_for_leg_id?: string;
  hedge_recycle?: boolean;

  // Optional fields
  symbol?: string;
  description?: string;
  
  // Flattened risk management fields (conditional based on enabled state)
  stop_loss?: {
    enabled: boolean;
    type: string;
    value: number;
    reinstate_overnight?: boolean;
  };
  target_profit?: {
    enabled: boolean;
    type: string;
    value: number;
  };
  trailing_stop_loss?: {
    enabled: boolean;
    type: string;
    instrument_move_value: number;
    stop_loss_move_value: number;
  };
  wait_and_trade?: {
    enabled: boolean;
    type: string;
    value: number;
    continue_overnight?: boolean;
    // UL W&T only — see backend PositionRecord.trade_only_first_entry. Ignored
    // server-side for premium W&T; sent regardless so the round-trip is byte-clean.
    trade_only_first_entry?: boolean;
  };
  re_entry?: {
    enabled: boolean;
    type: string;
    count: number;
  };
  re_execute?: {
    enabled: boolean;
    type: string;
    count: number;
  };
}

interface BackendStrategyData {
  name: string;
  description: string;
  underlying: string;              // Strategy-level: NIFTY/BANKNIFTY
  expiry_type: ExpiryType; // Strategy-level: weekly / next_weekly / monthly / next_monthly
  product: 'NRML' | 'MIS';
  entry_time: string;
  exit_time: string;
  entry_days: string[];
  exit_days: string[];
  // INTRADAY entry selection (optional for back-compat). Backend defaults to
  // WEEKDAY when missing.
  entry_selection_mode?: 'WEEKDAY' | 'DTE';
  entry_dtes?: number[];
  legs: BackendLeg[];
  
  // Trading type configuration
  trading_type: 'INTRADAY' | 'POSITIONAL';  // MANDATORY
  
  // Conditional fields based on trading_type:
  intraday_exit_mode?: 'SAME_DAY' | 'NEXT_DAY_BTST' | null;  // Only for INTRADAY
  expiry_day_rollover?: boolean;  // Only valid when intraday_exit_mode=NEXT_DAY_BTST
  entry_trading_days_before_expiry?: number | null;  // Only for POSITIONAL
  exit_trading_days_before_expiry?: number | null;   // Only for POSITIONAL

  // Flattened strategy configuration fields
  range_breakout?: boolean;
  range_breakout_time?: string | null;
  orb_continue_overnight?: boolean;
  move_sl_to_cost?: boolean;
  target_profit?: {
    type: string;
    value: number;
  } | null;
  mtm_stop_loss?: {
    type: string;
    value: number;
  } | null;
}

class StrategyTransformationService {
  /**
   * Transform frontend StrategyWizardDialog data to backend API format
   * @param frontendData - Strategy data from frontend
   * @param strategyIndex - Strategy-level index selection (NIFTY/BANKNIFTY)
   * @param expiryType - Strategy-level expiry type selection (weekly/monthly)
   */
  transformToBackend(
    frontendData: FrontendStrategyData,
    strategyIndex: string,
    expiryType: ExpiryType
  ): BackendStrategyData {
    const { strategyName, index, config, legs } = frontendData;
    
    // Validate product type against trading configuration
    const productTypeError = getProductTypeValidationError(
      config.productType,
      config.tradingType,
      config.intradayExitMode
    );
    
    if (productTypeError) {
      throw new Error(`Product type validation failed: ${productTypeError}`);
    }
    
    // Auto-correct product type if needed (additional safety check)
    const { productType: validatedProductType, wasChanged } = autoCorrectProductType(
      config.productType,
      config.tradingType,
      config.intradayExitMode
    );
    
    if (wasChanged) {
      console.warn(`Product type auto-corrected from ${config.productType} to ${validatedProductType}`);
      config.productType = validatedProductType;
    }
    
    // Transform legs with flat selection criteria (no strategy-level duplication)
    const backendLegs: BackendLeg[] = legs.map(leg => {
      const backendLeg: BackendLeg = {
        ...(leg.id && !leg.id.startsWith('leg-') && { leg_id: leg.id }),
        option_type: leg.optionType,  // Direct pass-through: CE → CE, PE → PE
        action: leg.actionType,
        lots: leg.totalLots,

        // Dynamic strike selection criteria (flat structure)
        selection_method: leg.selectionMethod,
        selection_value: undefined,
        selection_operator: undefined,
      };

      // Map selection criteria to flat structure based on method
      switch (leg.selectionMethod) {
        case 'ATM_POINTS':
        case 'ATM_PERCENT':
          const parsedValue = parseStrikeValue(leg.strikePrice, leg.selectionMethod);
          backendLeg.selection_value = typeof parsedValue === 'number' ? parsedValue : parseFloat(parsedValue.toString());
          // selection_operator remains undefined for ATM methods
          break;

        case 'PREMIUM':
          backendLeg.selection_value = leg.premiumValue || undefined;
          backendLeg.selection_operator = leg.premiumOperator || undefined;
          break;

        case 'PERCENTAGE_OF_STRADDLE_PREMIUM':
          backendLeg.selection_value = leg.straddlePremiumPercentage || undefined;
          backendLeg.selection_operator = leg.straddlePremiumOperator || undefined;
          break;

        case 'STRADDLE_WIDTH':
          // Signed multiplier of ATM straddle premium. Allow 0 explicitly.
          backendLeg.selection_value = leg.straddleWidth !== undefined ? leg.straddleWidth : undefined;
          // selection_operator stays undefined (no scan involved)
          break;

        case 'PERCENTAGE_OF_UNDERLYING':
          backendLeg.selection_value = leg.underlyingPercentage !== undefined ? leg.underlyingPercentage : undefined;
          backendLeg.selection_operator = leg.underlyingPremiumOperator || undefined;
          break;
      }

      // Associate-hedge: a hedge is BUY-only and carries NO risk features. Emit
      // the linkage (backend's 1-based hedge_for_leg_index = the target main
      // leg's position in the sent legs array) and return early so no
      // SL/Target/TSL/WAT/re-entry/re-execute is ever attached to a hedge.
      if (leg.isHedge) {
        backendLeg.is_hedge = true;
        const targetIdx = legs.findIndex(l => l.id === leg.hedgeForLegId);
        backendLeg.hedge_for_leg_index = targetIdx >= 0 ? targetIdx + 1 : 0;
        // Per-leg recycle opt-in — emit only when set so a keep hedge stays byte-
        // identical to a pre-recycle payload (backend resolve_hedge_links strips falsy).
        if (leg.hedgeRecycle) backendLeg.hedge_recycle = true;
        return backendLeg;
      }

      // Add flattened risk management fields conditionally (with defensive checks)
      if (leg.stopLoss?.enabled) {
        backendLeg.stop_loss = {
          enabled: true,
          type: leg.stopLoss.type,
          value: leg.stopLoss.value,
          ...(leg.stopLoss.reinstateOvernight === false && { reinstate_overnight: false }),
        };
      }

      if (leg.targetProfit?.enabled) {
        backendLeg.target_profit = {
          enabled: true,
          type: leg.targetProfit.type,
          value: leg.targetProfit.value
        };
      }

      if (leg.trailingStopLoss?.enabled) {
        backendLeg.trailing_stop_loss = {
          enabled: true,
          type: leg.trailingStopLoss.type,
          instrument_move_value: leg.trailingStopLoss.instrumentMoveValue,
          stop_loss_move_value: leg.trailingStopLoss.stopLossMoveValue
        };
      }

      if (leg.waitAndTrade?.enabled) {
        // WAT carry-forward is semantically valid only for overnight-capable
        // modes (POSITIONAL or INTRADAY+NEXT_DAY_BTST). Sanitize here so a
        // stale continueOvernight from an earlier session (user picked BTST,
        // checked Overnight, then switched to SAME_DAY) never reaches the
        // backend. Defence-in-depth alongside the EOD-branch SAME_DAY guard.
        const watOvernightAllowed = (
          config.tradingType === 'POSITIONAL'
          || (config.tradingType === 'INTRADAY'
              && config.intradayExitMode === 'NEXT_DAY_BTST')
        );
        backendLeg.wait_and_trade = {
          enabled: true,
          type: leg.waitAndTrade.type,
          value: leg.waitAndTrade.value,
          continue_overnight: watOvernightAllowed
            ? (leg.waitAndTrade.continueOvernight || false)
            : false,
          // UL W&T only. Ignored server-side for premium W&T; send anyway so
          // the round-trip is byte-clean and a leg flipped from UL→premium and
          // back preserves the toggle. Mirrors backend
          // `PositionRecord.trade_only_first_entry` (default False).
          trade_only_first_entry: leg.waitAndTrade.tradeOnlyFirstEntry || false,
        };
      }

      if (leg.reEntry?.enabled) {
        backendLeg.re_entry = {
          enabled: true,
          type: leg.reEntry.type,
          count: leg.reEntry.count
        };
      }

      // Re-Execute requires a valid Target Profit — the runtime gate
      // canEnableReExecute(targetProfit) enforces this. Mirror the same gate
      // here so an inconsistent UI state (TP toggled off after enabling
      // Re-Execute) doesn't ship `re_execute.enabled=true` to the backend.
      if (leg.reExecute?.enabled && leg.targetProfit?.enabled) {
        backendLeg.re_execute = {
          enabled: true,
          type: leg.reExecute.type,
          count: leg.reExecute.count
        };
      }
      
      return backendLeg;
    });
    
    // Format time strings
    const entryTime = `${config.entryTimeHour}:${config.entryTimeMinute}`;
    const exitTime = `${config.exitTimeHour}:${config.exitTimeMinute}`;
    
    const backendData: BackendStrategyData = {
      name: strategyName,
      description: `${index} strategy with ${legs.length} legs`,
      underlying: index, // NIFTY, BANKNIFTY, etc. (strategy-level)
      expiry_type: expiryType, // weekly/monthly (strategy-level)
      product: config.productType, // Use user-selected product type
      entry_time: entryTime,
      exit_time: exitTime,
      entry_days: config.entryDays,
      exit_days: config.exitDays,
      legs: backendLegs,
      
      // Trading type configuration (always required)
      trading_type: config.tradingType,
      
      // Conditional fields — send null to clear stale values when switching modes
      intraday_exit_mode: config.tradingType === 'INTRADAY' ? config.intradayExitMode : null,
      // expiry_day_rollover: only meaningful for INTRADAY+NEXT_DAY_BTST.
      // For next_weekly / next_monthly BTST, always send true — rollover is
      // semantically implied by the "next" expiry type choice and the backend
      // will also enforce this. For other BTST strategies, honour the toggle.
      expiry_day_rollover: (() => {
        if (config.tradingType !== 'INTRADAY' || config.intradayExitMode !== 'NEXT_DAY_BTST') {
          return false;
        }
        const isNextExpiry =
          config.expiryType === 'next_weekly' || config.expiryType === 'next_monthly';
        return isNextExpiry ? true : !!config.expiryDayRollover;
      })(),
      entry_trading_days_before_expiry: config.tradingType === 'POSITIONAL' ? config.entryTradingDaysBeforeExpiry : null,
      exit_trading_days_before_expiry: config.tradingType === 'POSITIONAL' ? config.exitTradingDaysBeforeExpiry : null,

      // INTRADAY entry-selection mode + DTEs. Sent for INTRADAY only; POSITIONAL
      // backend strips these. Both fields persist across mode toggles so the
      // user can flip Weekday ↔ DTE without losing the inactive selection.
      entry_selection_mode: config.tradingType === 'INTRADAY'
        ? (config.entrySelectionMode || 'WEEKDAY')
        : undefined,
      entry_dtes: config.tradingType === 'INTRADAY'
        ? (config.entryDtes || [])
        : undefined,

      // Flattened strategy configuration
      range_breakout: config.rangeBreakout,
      range_breakout_time: config.rangeBreakout ? `${config.rangeBreakoutTimeHour}:${config.rangeBreakoutTimeMinute}` : null,
      // ORB carry-forward is meaningful only when range_breakout is on AND
      // the strategy is overnight-capable (NRML + (POSITIONAL or INTRADAY+
      // NEXT_DAY_BTST)). SAME_DAY intraday enters and exits within one trading
      // day, so preserving an unfilled entry to tomorrow would create a
      // position with no valid exit_time — sanitize to false here in addition
      // to the EOD-branch guard so the persisted row stays coherent.
      orb_continue_overnight: (
        config.rangeBreakout
        && config.productType === 'NRML'
        && (config.tradingType === 'POSITIONAL'
            || (config.tradingType === 'INTRADAY'
                && config.intradayExitMode === 'NEXT_DAY_BTST'))
      ) ? !!config.orbContinueOvernight : false,
      move_sl_to_cost: config.moveSlToCost,
      target_profit: (config.targetProfit?.value || 0) > 0 ? {
        type: config.targetProfit?.type || 'TOTAL_MTM',
        value: config.targetProfit?.value || 0
      } : null,
      mtm_stop_loss: (config.mtmStopLoss?.value || 0) > 0 ? {
        type: config.mtmStopLoss?.type || 'TOTAL_MTM',
        value: config.mtmStopLoss?.value || 0
      } : null
    };
    
    return backendData;
  }
  
  /**
   * Parse and validate time string in HH:MM format
   * Returns default values for invalid input
   */
  private parseTimeString(
    timeString: string | undefined, 
    defaultHour: string, 
    defaultMinute: string
  ): [string, string] {
    // If no time string provided, use defaults
    if (!timeString) {
      return [defaultHour, defaultMinute];
    }
    
    // Check if string matches HH:MM format (supports H:MM and HH:MM)
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    if (!timeRegex.test(timeString)) {
      return [defaultHour, defaultMinute];
    }
    
    const [hour, minute] = timeString.split(':');
    return [hour.padStart(2, '0'), minute.padStart(2, '0')];
  }
  
  /**
   * Transform backend strategy data to frontend format
   */
  transformToFrontend(backendData: any, basketId: string): Partial<FrontendStrategyData> {
    const { name, strategy_name, underlying, legs = [], entry_time, exit_time } = backendData;
    
    // Transform backend legs to frontend format
    const frontendLegs: Leg[] = legs.map((backendLeg: any, index: number) => {
      const leg: Leg = {
        id: backendLeg.leg_id || `leg-${index}`,
        optionType: backendLeg.option_type,  // Direct pass-through: CE → CE, PE → PE
        actionType: backendLeg.action,
        strikePrice: formatStrikeForDisplay(backendLeg.selection_value || 0, backendLeg.selection_method || 'ATM_POINTS'),
        totalLots: backendLeg.lots || 1,
        selectionMethod: backendLeg.selection_method || 'ATM_POINTS',
        
        // Method-specific field mapping from new selection_value/selection_operator architecture
        // Finite-number guard on every method's numeric selection_value so a
        // corrupt DDB row with NaN doesn't leak into leg state (would render
        // empty in the input and hit an opaque backend 400 on save-without-edit).
        // Same shape as the STRADDLE_WIDTH / PERCENTAGE_OF_UNDERLYING branches below.
        premiumOperator: backendLeg.selection_method === 'PREMIUM' ? (backendLeg.selection_operator || 'CLOSEST') : 'CLOSEST',
        premiumValue: backendLeg.selection_method === 'PREMIUM' ? (typeof backendLeg.selection_value === 'number' && Number.isFinite(backendLeg.selection_value) ? backendLeg.selection_value : 0) : 0,
        straddlePremiumOperator: backendLeg.selection_method === 'PERCENTAGE_OF_STRADDLE_PREMIUM' ? (backendLeg.selection_operator || 'CLOSEST') : 'CLOSEST',
        straddlePremiumPercentage: backendLeg.selection_method === 'PERCENTAGE_OF_STRADDLE_PREMIUM' ? (typeof backendLeg.selection_value === 'number' && Number.isFinite(backendLeg.selection_value) ? backendLeg.selection_value : 5) : 5,
        straddleWidth: backendLeg.selection_method === 'STRADDLE_WIDTH' ? (typeof backendLeg.selection_value === 'number' && Number.isFinite(backendLeg.selection_value) ? backendLeg.selection_value : 0) : 0,
        underlyingPremiumOperator: backendLeg.selection_method === 'PERCENTAGE_OF_UNDERLYING' ? (backendLeg.selection_operator || 'CLOSEST') : 'CLOSEST',
        underlyingPercentage: backendLeg.selection_method === 'PERCENTAGE_OF_UNDERLYING' ? (typeof backendLeg.selection_value === 'number' && Number.isFinite(backendLeg.selection_value) ? backendLeg.selection_value : UNDERLYING_PCT.DEFAULT) : UNDERLYING_PCT.DEFAULT,
        
        // Risk Management Fields - transform from flattened backend structure
        stopLoss: {
          enabled: backendLeg.stop_loss?.enabled || false,
          type: backendLeg.stop_loss?.type || 'PERCENTAGE',
          value: backendLeg.stop_loss?.value || 0,
          // Missing = ticked; null / 0 / false = unticked (engine: should_skip_overnight_sl).
          reinstateOvernight: backendLeg.stop_loss?.reinstate_overnight === undefined
            || !!backendLeg.stop_loss.reinstate_overnight,
        },
        targetProfit: {
          enabled: backendLeg.target_profit?.enabled || false,
          type: backendLeg.target_profit?.type || 'PERCENTAGE',
          value: backendLeg.target_profit?.value || 0,
        },
        trailingStopLoss: {
          enabled: backendLeg.trailing_stop_loss?.enabled || false,
          type: backendLeg.trailing_stop_loss?.type || 'POINTS',
          instrumentMoveValue: backendLeg.trailing_stop_loss?.instrument_move_value || 0,
          stopLossMoveValue: backendLeg.trailing_stop_loss?.stop_loss_move_value || 0,
        },
        waitAndTrade: {
          enabled: backendLeg.wait_and_trade?.enabled || false,
          type: backendLeg.wait_and_trade?.type || 'PERCENTAGE',
          value: backendLeg.wait_and_trade?.value || 0,
          continueOvernight: backendLeg.wait_and_trade?.continue_overnight || false,
          // UL W&T only; falsy default is byte-clean for legacy premium rows.
          tradeOnlyFirstEntry: backendLeg.wait_and_trade?.trade_only_first_entry || false,
        },
        reEntry: {
          enabled: backendLeg.re_entry?.enabled || false,
          type: backendLeg.re_entry?.type || 'SL_REENTRY',
          count: backendLeg.re_entry?.count || 1,
        },
        reExecute: {
          enabled: backendLeg.re_execute?.enabled || false,
          type: backendLeg.re_execute?.type || 'TP_REEXEC',
          count: backendLeg.re_execute?.count || 1,
        },
        // Associate-hedge: the backend returns the resolved hedge_for_leg_id (the
        // main's leg_id). Each frontend leg's `id` IS its backend leg_id (see
        // above), so that value is exactly the target main leg's frontend id.
        isHedge: backendLeg.is_hedge || undefined,
        hedgeForLegId: backendLeg.is_hedge ? (backendLeg.hedge_for_leg_id || undefined) : undefined,
        hedgeRecycle: backendLeg.is_hedge ? (backendLeg.hedge_recycle || undefined) : undefined,
      };

      return leg;
    });
    
    // Parse time strings with robust validation and defaults
    const [entryHour, entryMinute] = this.parseTimeString(entry_time, '09', '15');
    const [exitHour, exitMinute] = this.parseTimeString(exit_time, '15', '30');
    const [rangeBreakoutHour, rangeBreakoutMinute] = this.parseTimeString(
      backendData.range_breakout_time, 
      '09', 
      '30'
    );
    
    // Transform strategy configuration (includes expiryType)
    const config: FrontendStrategyConfig = {
      entryTimeHour: entryHour,
      entryTimeMinute: entryMinute,
      exitTimeHour: exitHour,
      exitTimeMinute: exitMinute,
      rangeBreakout: backendData.range_breakout || false,
      rangeBreakoutTimeHour: rangeBreakoutHour,
      rangeBreakoutTimeMinute: rangeBreakoutMinute,
      orbContinueOvernight: backendData.orb_continue_overnight || false,
      moveSlToCost: backendData.move_sl_to_cost || false,
      expiryType: backendData.expiry_type || EXPIRY_TYPE.WEEKLY,
      productType: backendData.product || 'MIS',
      tradingType: backendData.trading_type || 'INTRADAY',
      intradayExitMode: backendData.intraday_exit_mode || 'SAME_DAY',
      expiryDayRollover: !!backendData.expiry_day_rollover,
      // POSITIONAL strategies are persisted with entry_days/exit_days = [] (backend strips them
      // in strategy_manager._validate_schedule_fields). Do NOT simplify this to `|| WEEKDAYS` —
      // empty arrays are truthy in JS, so the form would load with no days selected, and switching
      // the trading type to INTRADAY would submit [] and fail backend validation
      // ("INTRADAY strategies require non-empty entry_days and exit_days").
      entryDays: backendData.entry_days && backendData.entry_days.length > 0 ? backendData.entry_days : WEEKDAYS,
      exitDays: backendData.exit_days && backendData.exit_days.length > 0 ? backendData.exit_days : WEEKDAYS,
      // Back-compat: existing strategies have neither entry_selection_mode nor
      // entry_dtes; default to WEEKDAY + [] so the form opens on the existing
      // weekday-mode UI without flicker. Preserve both fields when present so
      // toggling modes never loses the inactive selection.
      entrySelectionMode: backendData.entry_selection_mode === 'DTE' ? 'DTE' : 'WEEKDAY',
      entryDtes: Array.isArray(backendData.entry_dtes)
        ? backendData.entry_dtes.map((d: number | string) => Number(d))
        : [],
      entryTradingDaysBeforeExpiry: backendData.entry_trading_days_before_expiry ?? 4,
      exitTradingDaysBeforeExpiry: backendData.exit_trading_days_before_expiry ?? 0,
      targetProfit: {
        type: backendData.target_profit?.type || 'TOTAL_MTM',
        value: backendData.target_profit?.value || 0,
      },
      mtmStopLoss: {
        type: backendData.mtm_stop_loss?.type || 'TOTAL_MTM',
        value: backendData.mtm_stop_loss?.value || 0,
      },
    };
    
    return {
      strategyId: backendData.strategy_id || undefined,  // Only include if exists
      basketId,
      strategyName: name || strategy_name || 'Imported Strategy',
      index: underlying || 'NIFTY',
      config,
      legs: frontendLegs,
    };
  }
  
  /**
   * Transform strike price based on selection method
   * Now extracts numeric values for ATM_POINTS and ATM_PERCENT methods
   */
  private transformStrike(strikePrice: string, selectionMethod: string): number | string {
    // Use the new parser to extract numeric values for backend API
    const transformedValue = parseStrikeValue(strikePrice, selectionMethod as any);
    
    // Debug logging to verify transformation
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log(`Strike transformation [${selectionMethod}]: "${strikePrice}" → ${JSON.stringify(transformedValue)}`);
    }
    
    return transformedValue;
  }
  
  
  /**
   * Validate frontend data before transformation using basic validation
   */
  validateFrontendData(data: FrontendStrategyData): { isValid: boolean; errors: string[]; warnings?: string[] } {
    // Basic validation - for advanced validation use validateFrontendDataAdvanced
    const errors: string[] = [];
    
    // Basic validation
    if (!data.strategyName?.trim()) {
      errors.push('Strategy name is required');
    }
    
    if (!data.basketId?.trim()) {
      errors.push('Basket ID is required');
    }
    
    if (!data.legs || data.legs.length === 0) {
      errors.push('At least one position is required');
    }
    
    // Validate each leg
    data.legs?.forEach((leg, index) => {
      if (leg.totalLots <= 0) {
        errors.push(`Position ${index + 1}: Total lots must be greater than 0`);
      }

      if (leg.totalLots > 200) {
        errors.push(`Position ${index + 1}: Total lots cannot exceed 200`);
      }
      
      if (leg.selectionMethod === 'PREMIUM') {
        if (!leg.premiumOperator || leg.premiumValue === undefined) {
          errors.push(`Position ${index + 1}: Premium criteria required for Premium selection`);
        }
      }
      
      if (leg.selectionMethod === 'PERCENTAGE_OF_STRADDLE_PREMIUM') {
        if (!leg.straddlePremiumOperator || leg.straddlePremiumPercentage === undefined) {
          errors.push(`Position ${index + 1}: Straddle premium criteria required for Percentage of Straddle Premium selection`);
        }
      }

      if (leg.selectionMethod === 'STRADDLE_WIDTH') {
        if (leg.straddleWidth === undefined || leg.straddleWidth < -10 || leg.straddleWidth > 10) {
          errors.push(`Position ${index + 1}: Straddle width must be between -10 and 10`);
        }
      }

      if (leg.selectionMethod === 'PERCENTAGE_OF_UNDERLYING') {
        errors.push(...validatePercentageOfUnderlying(leg, `Position ${index + 1}:`));
      }

      // Validate strike value format for ATM_POINTS and ATM_PERCENT methods
      if (leg.selectionMethod === 'ATM_POINTS' || leg.selectionMethod === 'ATM_PERCENT') {
        if (!validateStrikeFormat(leg.strikePrice, leg.selectionMethod)) {
          errors.push(`Position ${index + 1}: Invalid strike price format "${leg.strikePrice}" for ${leg.selectionMethod} method`);
        }
      }
    });
    
    // Validate time configuration
    const entryHour = parseInt(data.config.entryTimeHour);
    const exitHour = parseInt(data.config.exitTimeHour);
    const entryMinute = parseInt(data.config.entryTimeMinute);
    const exitMinute = parseInt(data.config.exitTimeMinute);
    const entryTimeMinutes = entryHour * 60 + entryMinute;
    const exitTimeMinutes = exitHour * 60 + exitMinute;
    
    if (entryHour < 9 || entryHour > 15) {
      errors.push('Entry time must be between 09:16 and 15:39');
    }

    if (exitHour < 9 || exitHour > 15) {
      errors.push('Exit time must be between 09:16 and 15:39');
    }
    
    // POSITIONAL trading days validation
    if (data.config.tradingType === 'POSITIONAL') {
      const entryDays = data.config.entryTradingDaysBeforeExpiry;
      const exitDays = data.config.exitTradingDaysBeforeExpiry;

      if (entryDays < exitDays) {
        errors.push('Entry trading days must be greater than or equal to exit trading days for positional strategies');
      }

      // Same-day POSITIONAL trades need intraday time validation
      if (entryDays === exitDays && entryTimeMinutes >= exitTimeMinutes) {
        errors.push('Entry time must be before exit time for same-day positional strategies');
      }
    } else {
      // INTRADAY time validation with proper BTST support
      const isNextDayExit = data.config.tradingType === 'INTRADAY' && data.config.intradayExitMode === 'NEXT_DAY_BTST';

      if (!isNextDayExit && entryTimeMinutes >= exitTimeMinutes) {
        errors.push('Entry time must be before exit time');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate frontend data with advanced validation (async)
   */
  async validateFrontendDataAdvanced(data: FrontendStrategyData): Promise<{ isValid: boolean; errors: string[]; warnings: string[] }> {
    return strategyValidationService.validateStrategy(data);
  }
  
  /**
   * Create a strategy creation API call payload
   */
  createApiPayload(
    _basketId: string,
    frontendData: FrontendStrategyData,
    strategyIndex: string,
    expiryType: ExpiryType
  ): BackendStrategyData {
    const validation = this.validateFrontendData(frontendData);

    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    return this.transformToBackend(frontendData, strategyIndex, expiryType);
  }
}

const strategyTransformationService = new StrategyTransformationService();
export default strategyTransformationService;
export { StrategyTransformationService };
export type { FrontendStrategyData, BackendStrategyData, BackendLeg };